import { useSetSearchParam } from '@/lib/searchParams';
import type { QuerySort } from '@rock/db-admin';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export type SortDirection = QuerySort['dir'];

/**
 * Which column a table is sorted by, identified by COLUMN key rather than Firestore field — the two
 * differ wherever a column's label and its stored field do ({@link resolveQuerySort} maps one to the
 * other).
 */
export type TableSort = { key: string; dir: SortDirection };

/** What {@link resolveQuerySort} and {@link useServerSort} need of a column; `DataTableColumn` satisfies it. */
export type SortableColumn = { key: string; sortField?: string };

/** What a column can be sorted on client-side. Missing values always land last, either direction. */
export type SortValue = string | number | boolean | undefined | null;

const SORT_PARAM = 'sort';

/**
 * A table's sort, backed by a URL search param (`?sort=updatedAt:desc`). The URL is the source of
 * truth so it survives the row-click → details → back round trip, the same way these pages' filters
 * already do — and so a sorted view is a link someone can paste.
 *
 * `undefined` means the table's own default ordering, which is what the third tap on a header returns
 * to (see {@link nextSort}).
 */
export function useTableSort(paramKey: string = SORT_PARAM): {
    sort: TableSort | undefined;
    setSort: (sort: TableSort | undefined) => void;
} {
    const [searchParams] = useSearchParams();
    const setSearchParam = useSetSearchParam();
    const sort = parseTableSort(searchParams.get(paramKey));
    const setSort = useCallback(
        (next: TableSort | undefined) => setSearchParam(paramKey, next ? `${next.key}:${next.dir}` : undefined),
        [setSearchParam, paramKey],
    );
    return { sort, setSort };
}

/**
 * The whole server-side sorting wire for one table: reads the sorted column from the URL and keeps the
 * paginated atom's `sort` in step with it. Returns what {@link DataTable}'s `sort`/`onSortChange` want.
 *
 * This is the step that turns a header tap into a different Firestore query, so it belongs in one place
 * rather than in each page — a table wired up without it would render arrows, write the URL, and sort
 * nothing.
 */
export function useServerSort<C extends SortableColumn>(
    columns: C[],
    query: { sort: QuerySort | undefined; setSort: (sort: QuerySort | undefined) => void },
    sortDisabled?: (column: C) => string | undefined,
): { sort: TableSort | undefined; setSort: (sort: TableSort | undefined) => void } {
    const { sort: tableSort, setSort: setTableSort } = useTableSort();
    // Deliberately not memoized: the predicate's answer changes with the page's own state (a search
    // box filling in), so a memo keyed on the column list would go stale. It's a `find` over a handful
    // of columns, and the field/dir primitives below keep the effect from re-firing.
    const querySort = resolveQuerySort(columns, tableSort, sortDisabled);
    const { sort: currentSort, setSort: setQuerySort } = query;
    const field = querySort?.field;
    const dir = querySort?.dir;

    useEffect(() => {
        if (currentSort?.field !== field || currentSort?.dir !== dir) {
            setQuerySort(field && dir ? { field, dir } : undefined);
        }
    }, [currentSort, field, dir, setQuerySort]);

    return { sort: tableSort, setSort: setTableSort };
}

/**
 * Translates the sorted COLUMN into the stored field its query orders by. Returns undefined when
 * nothing is sorted, when the sorted column has no stored field, or when it can't sort right now —
 * all of which mean "use the DAO's default ordering". Deciding that here as well as in the table is
 * what keeps the header and the query from disagreeing about what's sorted.
 */
export function resolveQuerySort<C extends SortableColumn>(
    columns: C[],
    sort: TableSort | undefined,
    sortDisabled?: (column: C) => string | undefined,
): QuerySort | undefined {
    if (!sort) return undefined;
    const column = columns.find((candidate) => candidate.key === sort.key);
    if (!column?.sortField || sortDisabled?.(column)) return undefined;
    return { field: column.sortField, dir: sort.dir };
}

/** Parses the `key:dir` param form. Anything malformed reads as "no sort" rather than throwing. */
export function parseTableSort(raw: string | null | undefined): TableSort | undefined {
    if (!raw) return undefined;
    const [key, dir] = raw.split(':');
    if (!key || (dir !== 'asc' && dir !== 'desc')) return undefined;
    return { key, dir };
}

/**
 * The tri-state header cycle: first tap sorts by the column, second reverses it, third clears back to
 * the table's default ordering. `firstDir` is the direction a first tap applies — dates want 'desc',
 * since "newest first" is what someone means by sorting a date column.
 */
export function nextSort(
    current: TableSort | undefined,
    key: string,
    firstDir: SortDirection = 'asc',
): TableSort | undefined {
    if (current?.key !== key) return { key, dir: firstDir };
    if (current.dir === firstDir) return { key, dir: firstDir === 'asc' ? 'desc' : 'asc' };
    return undefined;
}

/**
 * Client-side ordering, for tables that hold every row already. Missing values sort last in BOTH
 * directions — an empty cell is never the answer to "show me the top" — so the direction flips the
 * populated rows only. (Server-side sorting can't do this: Firestore drops documents missing the
 * ordered field entirely, which is what a column's `sortNote` warns about.)
 */
export function sortRows<T>(rows: T[], sortBy: (row: T) => SortValue, dir: SortDirection): T[] {
    const sign = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const aValue = sortBy(a);
        const bValue = sortBy(b);
        const aMissing = isMissing(aValue);
        const bMissing = isMissing(bValue);
        if (aMissing || bMissing) return aMissing === bMissing ? 0 : aMissing ? 1 : -1;
        return sign * compareValues(aValue, bValue);
    });
}

const isMissing = (value: SortValue) => value === undefined || value === null || value === '';

/** Mixed types compare as strings: `Number('abc')` is NaN, and a NaN comparator orders nothing at all. */
function compareValues(a: SortValue, b: SortValue): number {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
    return `${a}`.localeCompare(`${b}`);
}
